import { MissingPantoneColorsError } from "../assets/errors/MissingPantoneColorsError";
import { MissingSpotColorsError } from "../assets/errors/MissingSpotColorsError";
import { pantoneColors } from "../assets/pantoneColors";
import { spotColors } from "../assets/spotColors";
import { EMBROIDERY_THREAD_COLORS } from "../assets/threadColors";
import { convertCMYKToRGB } from "../conversion";
import { isCmyk, isRgb, isCmyka, isRgba } from "../typeDetection";
import { InvalidPantoneColorError, InvalidSpotColorError, UnknownThreadColorError } from "./errors";
import { convertHexColorToRGB } from "./hex2rgb";
export function parseColor(color) {
    if (isCmyk(color)) {
        return { colorSpace: "cmyk", value: color };
    }
    if (isRgb(color)) {
        return { colorSpace: "rgb", value: color };
    }
    if (color.match(/^rgb\(([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3})\)$/)) {
        const colors = color.match(/[0-9]{1,3}/g); // eslint-disable-line @typescript-eslint/no-non-null-assertion
        return { value: { r: +colors[0], g: +colors[1], b: +colors[2] }, colorSpace: "rgb" };
    }
    if (color.match(/^rgba\(([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3})\)$/)) {
        const colors = color.match(/[0-9]{1,3}/g); // eslint-disable-line @typescript-eslint/no-non-null-assertion
        return { value: { r: +colors[0], g: +colors[1], b: +colors[2], a: +colors[3] }, colorSpace: "rgb" };
    }
    // TODO: is rgba(#000000) actually a valid CimDoc color? It's not documented to be.
    if (color.match(/(^rgba?\((#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6})\))$/)) {
        const match = color.match(/rgba?\((.*)\)/); // eslint-disable-line @typescript-eslint/no-non-null-assertion
        return { value: convertHexColorToRGB(match[1]), colorSpace: "rgb" }; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }
    if (color.match(/^(#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3})$/)) {
        return { value: convertHexColorToRGB(color), colorSpace: "rgb" }; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }
    if (color.match(/^cmyka\(([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3})\)$/)) {
        const colors = color.match(/[0-9]{1,3}/g); // eslint-disable-line @typescript-eslint/no-non-null-assertion
        return {
            value: { c: +colors[0], m: +colors[1], y: +colors[2], k: +colors[3], a: +colors[4] },
            colorSpace: "cmyk",
        };
    }
    if (color.match(/^cmyk\(([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3}),\s*([0-9]{1,3})\)$/)) {
        const colors = color.match(/[0-9]{1,3}/g); // eslint-disable-line @typescript-eslint/no-non-null-assertion
        return { value: { c: +colors[0], m: +colors[1], y: +colors[2], k: +colors[3] }, colorSpace: "cmyk" };
    }
    if (color.match(/(^pantone\((.*)\))$/)) {
        return { value: color, colorSpace: "pantone" };
    }
    if (color.match(/(^thread\((.*)\))$/)) {
        return { value: color, colorSpace: "thread" };
    }
    if (color.match(/(^spot\((.*)\))$/)) {
        return { value: color, colorSpace: "spot" };
    }
    return undefined;
}
function rgbaColorToRenderString(color, alphaFromZeroToOne) {
    return `rgba(${color.r},${color.g},${color.b},${alphaFromZeroToOne})`;
}
export function convertParsedColorToRGBA(color) {
    if (color === undefined) {
        return undefined;
    }
    switch (color.colorSpace) {
        case "rgb":
            return Object.assign({ a: 255 }, color.value);
        case "cmyk": {
            const rgbColor = convertCMYKToRGB(color.value);
            if (isCmyka(color.value)) {
                return Object.assign({ a: (color.value.a * 255) / 100 }, rgbColor);
            }
            else {
                return Object.assign({ a: 255 }, rgbColor);
            }
        }
        case "pantone":
            return pantoneColorToRGBA(color).rgba;
        case "spot":
            return spotColorToRGBA(color).rgba;
        case "thread":
            return threadColorToRGBA(color).rgba;
    }
}
/** Convert a parsed color to a color that can be rendered onto an HTMLCanvas
 * Requires initialization to handle pantone and spot colors.
 * @see preFetch
 */
export function convertParsedColorToRenderColor(originalValue, parsedColor) {
    if (parsedColor === undefined) {
        return undefined;
    }
    switch (parsedColor.colorSpace) {
        case "cmyk": {
            const rgbColor = convertCMYKToRGB(parsedColor.value);
            return { originalValue, type: "color", display: rgbaColorToRenderString(rgbColor, isCmyka(parsedColor.value) ? parsedColor.value.a / 100 : 1) };
        }
        case "rgb": {
            const alpha = isRgba(parsedColor.value) ? parsedColor.value.a / 255 : 1;
            return { originalValue, type: "color", display: rgbaColorToRenderString(parsedColor.value, alpha) };
        }
        case "pantone": {
            const { name, rgba } = pantoneColorToRGBA(parsedColor);
            return {
                type: "color",
                name,
                display: rgbaColorToRenderString(rgba, rgba.a / 255),
                originalValue,
            };
        }
        case "spot": {
            return spotColorToRenderColor(parsedColor);
        }
        case "thread": {
            const { threadValue, rgba } = threadColorToRGBA(parsedColor);
            return {
                type: "color",
                name: threadValue,
                display: rgbaColorToRenderString(rgba, 1),
                originalValue,
            };
        }
        default:
            throw new Error("Invalid color type");
    }
}
/** Convert any valid CDIF color string to a color that can be rendered on an HTMLCanvas
 * Requires initialization to handle pantone and spot colors.
 * @see preFetch
 */
export function parseColorForRender(color) {
    const parsedColor = parseColor(color);
    return convertParsedColorToRenderColor(color, parsedColor);
}
function regExMatch(value, matcher) {
    const result = value.match(matcher);
    if (!result) {
        throw new Error(`Regex error for value ${value} with matcher ${matcher}`);
    }
    return result;
}
export function splitOnce(s, delimiter) {
    const index = s.indexOf(delimiter);
    if (index > -1) {
        return [s.slice(0, index), s.slice(index + delimiter.length)];
    }
    return [s];
}
function spotColorToRGBA(color) {
    const [, , spotNameValue] = regExMatch(color.value, /(^spot\((.*)\))$/);
    const [spotName, intensityOrEmbeddedRgba = "100"] = splitOnce(spotNameValue, ",");
    // Spot color can have an embedded value like: spot(Engraving, rgba(#015294))
    // If there is a value of length > 3 it's most likely an embedded color
    // Else it's the intensity
    if (intensityOrEmbeddedRgba.trim().length > 3) {
        const embeddedRgba = intensityOrEmbeddedRgba.trim();
        const rgbaValue = convertParsedColorToRGBA(parseColor(embeddedRgba));
        if (rgbaValue) {
            return { name: spotName, rgba: rgbaValue };
        }
    }
    if (!spotColors) {
        throw new MissingSpotColorsError();
    }
    const spotColor = spotColors.find(({ Name }) => Name === spotName.trim());
    if (!spotColor) {
        throw new InvalidSpotColorError();
    }
    // No color, just a name for a channel
    if (spotColor.HiddenOnPreview) {
        return { name: spotColor.Name };
    }
    // Name and visible color
    return {
        name: spotColor.Name,
        rgba: {
            r: Number(spotColor.AlternateColor.RgbColor.R),
            g: Number(spotColor.AlternateColor.RgbColor.G),
            b: Number(spotColor.AlternateColor.RgbColor.B),
            a: (Number(intensityOrEmbeddedRgba) * 255) / 100,
        },
    };
}
function spotColorToRenderColor(color) {
    const { name, rgba } = spotColorToRGBA(color);
    if (rgba) {
        return {
            type: "color",
            name,
            originalValue: color.value,
            display: rgbaColorToRenderString(rgba, rgba.a / 255),
        };
    }
    return {
        type: "color",
        name,
        originalValue: color.value,
    };
}
function pantoneColorToRGBA(color) {
    if (!pantoneColors) {
        throw new MissingPantoneColorsError();
    }
    const pantoneValue = color.value.replace(/pantone\(|\)/g, "");
    const [pantoneName, intensity = "100"] = splitOnce(pantoneValue, ",");
    const pantone = pantoneColors.find(({ Name }) => Name === pantoneName.trim());
    if (!pantone) {
        throw new InvalidPantoneColorError();
    }
    return { name: pantone.Name, rgba: { r: Number(pantone.R), g: Number(pantone.G), b: Number(pantone.B), a: (Number(intensity) * 255) / 100 } };
}
function threadColorToRGBA(color) {
    const threadValue = color.value.replace(/thread\(|\)/g, "");
    const threadRgb = EMBROIDERY_THREAD_COLORS[threadValue];
    if (threadRgb === undefined) {
        throw new UnknownThreadColorError();
    }
    return { threadValue, rgba: Object.assign({ a: 255 }, convertHexColorToRGB(threadRgb)) };
}
//# sourceMappingURL=parseColor.js.map